import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { ErrorNote } from "@/components/ErrorNote";
import { ActionRow } from "@/components/ActionRow";
import { products, type PermMapRow, type SpacePermMapRow } from "@/api/client";
import { Select } from "@/components/inputs";
import { qk, usePermissionMaps, useSpacePermissions, useSpaces } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

import { Icon } from "@/components/icons";
/** A product's permission vocabulary, and what each SPACE grants of it.
 *
 *  TWO JOBS, ONE SCREEN, SELECTED BY THE SPACE FIELD. Dattatray: *"Each
 *  product shall have its permission map configurable (fixed one time) by
 *  super admin ... fix the permission map once for each space inside of that
 *  product."* The vocabulary and the ticking are the same list of
 *  permissions asked two questions — *what exists* and *what does this space
 *  grant* — so they are one table, not two pages that would drift apart the
 *  first time somebody added a permission to only one of them.
 *
 *    Space = "All spaces"   the vocabulary. Authoring: add a map, a group,
 *                           a permission; deprecate one.
 *    Space = a space        the same rows with a GRANTS tick, and Save /
 *                           Discard at the foot. Authoring is hidden: you
 *                           are answering a different question, and a
 *                           control that edits the vocabulary while you are
 *                           allocating it is how the wrong thing gets
 *                           clicked.
 *
 *  PER (PRODUCT, TOOL), not per product. D-89 recorded "the map goes to kind"
 *  and was incomplete: Cluster carries BOTH a `uds_tool` map (6 groups, 21
 *  permissions) and an `eol_tool` map (1 group, 5). The same integer means
 *  different things in different tools, which is exactly why nothing outside
 *  a product may reason about the numbers.
 *
 *  THE NUMBERS ARE THE CONTRACT. Every deployed bench compares against these
 *  integers, so a permission's id is NOT editable once it exists — changing
 *  what 207 means silently changes what every bench holding it may do, with
 *  no error anywhere, because the tool simply believes the number. Deprecate
 *  and add a new one; the page offers exactly those two actions.
 *
 *  SUPER ADMIN ONLY, per Dattatray. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the vocabulary and no controls.
 */
export function PermissionMapPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [params, setParams] = useSearchParams();
  // ?space= arrives from the Spaces page, so "set what this space grants"
  // starts where the operator was already looking rather than asking them to
  // find the product and the space again.
  const space = params.get("space") ?? "";
  const setSpace = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set("space", v); else next.delete("space");
    setParams(next, { replace: true });
  };

  const [tool, setTool] = useState("");
  const q = usePermissionMaps(kind);
  const spacesQ = useSpaces(kind);
  const [addMap, setAddMap] = useState(false);

  const spaces = (spacesQ.data?.spaces ?? []).filter((sp) => !sp.archived);
  // A space id left over from another product would be a 404 nobody can
  // explain. Dropped as soon as the product's own list says it is not one.
  useEffect(() => {
    if (space && spacesQ.data && !spaces.some((sp) => sp.space_id === space)) setSpace("");
  }, [space, spacesQ.data, kind]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Tool permission map"
        desc={space
          ? "What this space grants by default. Every company allocated the space inherits these, and a permission left unticked is one the space does not grant."
          : "The vocabulary a product's tools use, per tool. The numbers are what a bench compares against, so they are never re-pointed - deprecate and add instead."}
      />

      {/* SECOND SELECTOR IS THE TOOL, NOT A MODEL. Dattatray confirmed the
          key: "(product, TOOL) is correct. We decided that we can allocate
          Tool release to a specific model - I think that was the catch (its
          part of tool release rule)." So model targeting lives on RELEASES;
          a map belongs to a product and a tool.

          SPACE SITS BETWEEN THEM because it is the wider cut: it changes the
          QUESTION the table answers, while the tool only narrows the rows. */}
      <ScopeBar right={mayAuthor && !space ? (
        <button className={addMap ? "btn ghost" : "btn teal"}
          disabled={!kind} title={!kind ? "Choose a product first" : undefined}
          onClick={() => setAddMap(!addMap)}>
          {addMap ? "Cancel" : "Add a tool map"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={(k) => { setKind(k); setTool(""); setSpace(""); }} />
        <ScopeField label="Space">
          <Select value={space} onChange={(e) => setSpace(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            {/* Not "All spaces" on its own: the reader would expect the union
                of what every space grants, which is a third thing this page
                does not show. */}
            <option value="">All spaces - the vocabulary</option>
            {spaces.map((sp) => (
              <option key={sp.space_id} value={sp.space_id}>{sp.name}</option>
            ))}
          </Select>
        </ScopeField>
        <ScopeField label="Tool">
          <Select value={tool} onChange={(e) => setTool(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            <option value="">All tools</option>
            {(q.data?.maps ?? []).map((m) => (
              <option key={m.map_id} value={m.tool}>{m.name}</option>
            ))}
          </Select>
        </ScopeField>
      </ScopeBar>

      {addMap && !space && <AddMapRow kind={kind} onDone={() => setAddMap(false)} />}

      {!kind && <ChoosePrompt what="permission map" />}
      {kind && space && (
        <SpaceGrants kind={kind} spaceId={space} tool={tool} mayAuthor={mayAuthor} />
      )}
      {kind && !space && (
        <Vocabulary kind={kind} tool={tool} mayAuthor={mayAuthor} />
      )}
    </div>
  );
}

/** What ONE space grants — the vocabulary with a tick on each row (D-102).
 *
 *  `space_definition_permissions` arrived in the Fabric port with 64 rows and
 *  was touched by exactly one query, a COUNT. Nothing read the values and
 *  nothing wrote them, so this — the screen Dattatray described as the whole
 *  point of spaces — could not be built.
 *
 *  THE SAVE REPLACES THE SET, and the foot says so in words, because the
 *  alternative reading is invisible: if an untick meant "leave as it was",
 *  nothing on this screen could ever revoke, and the operator would have no
 *  way to tell which of the two had happened.
 *
 *  ACTIONS AT THE FOOT. Dattatray, on the spec editor: *"we should have Save
 *  Spec and Discard button at the bottom instead of top"* — you finish
 *  ticking at the bottom of a long list, and a button at the top means
 *  scrolling back to a control you have stopped looking at.
 */
type Tickable = { perm_uid: string; default: boolean };

function SpaceGrants({ kind, spaceId, tool, mayAuthor }: {
  kind: string; spaceId: string; tool: string; mayAuthor: boolean;
}) {
  const qc = useQueryClient();
  const q = useSpacePermissions(kind, spaceId);
  /** Only what the operator CHANGED. Seeding a full draft from the server
   *  would make every later refetch look like a pending edit. */
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  const [openMaps, setOpenMaps] = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  useEffect(() => { setEdits({}); }, [kind, spaceId]);

  const maps: SpacePermMapRow[] = useMemo(
    () => (q.data?.maps ?? []).filter((m) => !tool || m.tool === tool),
    [q.data, tool]);

  // Narrowing to ONE tool opens it: choosing a tool IS the request to see it.
  // Everything else stays closed — Dattatray, on two maps open at once:
  // *"UI seems like too much is being shown."*
  useEffect(() => {
    setOpenMaps(tool && maps.length === 1 ? new Set([maps[0].map_id]) : new Set());
    setOpenGroups(new Set());
  }, [kind, spaceId, tool, maps.length]);

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  };

  const granted = (p: Tickable) => edits[p.perm_uid] ?? p.default;

  const save = useMutation({
    /** `mapId` is the map whose button was pressed.
     *
     *  THE ENDPOINT REPLACES THE WHOLE SET for the space, across every map,
     *  so this has to send every permission that should end up ON — including
     *  the other maps'. What it must NOT send is another map's UNSAVED edits:
     *  those belong to that map's own button, and sweeping them in is exactly
     *  the bug the single shared footer had.
     *
     *  So: this map's permissions take the edited value, every other map's
     *  take the value the SERVER currently holds.
     *
     *  ONLY THE ONES THAT ARE ON. A row storing `false` is indistinguishable
     *  in meaning from no row, and storing it would make the Spaces page's
     *  "grants N" count decisions rather than permissions.
     */
    mutationFn: (mapId: string) => {
      const on: Record<string, boolean> = {};
      for (const m of q.data?.maps ?? []) {
        for (const g of m.groups) {
          for (const p of g.permissions) {
            const value = m.map_id === mapId ? granted(p) : p.default;
            if (value) on[p.perm_uid] = true;
          }
        }
      }
      return products.setSpacePermissions(kind, spaceId, on);
    },
    onSuccess: (_r, mapId) => {
      // Clear only the map that was saved. The other map's edits are still
      // pending and its own button is still waiting to be pressed.
      setEdits((prev) => {
        const next = { ...prev };
        const m = (q.data?.maps ?? []).find((x) => x.map_id === mapId);
        for (const g of m?.groups ?? []) {
          for (const p of g.permissions) delete next[p.perm_uid];
        }
        return next;
      });
      void qc.invalidateQueries({ queryKey: qk.spacePerms(kind, spaceId) });
      void qc.invalidateQueries({ queryKey: qk.spaces(kind) });
    },
  });

  if (q.isLoading) return <div className="panel panel-pad cap">Loading…</div>;
  if (!q.data) return <div className="panel panel-pad cap">This space is not part of this product.</div>;
  if (!maps.length) {
    return <div className="panel panel-pad cap">
      This product has no permission map yet, so there is nothing for a space to grant.
    </div>;
  }

  return (
    <>
      <ErrorNote error={save.error} panel />

      {/* SAID ONCE, not per map. The rule is about the SPACE, and repeating it
          under every map would read as a different rule each time. */}
      <div className="cap" style={{ color: "var(--t-dim)", marginBottom: 8 }}>
        Saving replaces the whole set - an unticked permission is removed.
      </div>

      {maps.map((m) => {
        const mOpen = openMaps.has(m.map_id);
        const allGroups = m.groups.map((g) => g.group_uid);
        const allOpen = allGroups.length > 0 && allGroups.every((u) => openGroups.has(u));
        const mineOn = m.groups.reduce((k, g) => k + g.permissions.filter(granted).length, 0);
        const unsaved = m.groups.flatMap((g) => g.permissions)
          .filter((p) => edits[p.perm_uid] !== undefined
            && edits[p.perm_uid] !== p.default).length;
        return (
          <div key={m.map_id}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div className="section-label" style={{ cursor: "pointer" }}
                onClick={() => setOpenMaps((s) => toggle(s, m.map_id))}>
                <Caret open={mOpen} /> {m.name}
              </div>
              <span className="mono cap">{m.tool}</span>
              {/* THE COUNT IS ON THE CLOSED HEADER. Collapsing is only
                  acceptable if the header says what it is hiding - otherwise
                  the operator has to open every map to find where the ticks
                  are. */}
              <Chip tone="mute">{mineOn} of {m.permission_count} granted</Chip>
              {unsaved > 0 && <Chip tone="warn">{unsaved} unsaved</Chip>}
              {mOpen && m.groups.length > 1 && (
                <button className="btn ghost" style={{ marginLeft: "auto" }}
                  onClick={() => setOpenGroups((s) => {
                    const next = new Set(s);
                    for (const u of allGroups) { if (allOpen) next.delete(u); else next.add(u); }
                    return next;
                  })}>
                  {allOpen ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>

            {mOpen && (
              <GrantTree map={m} groupsOpen={openGroups} mayAuthor={mayAuthor}
                onToggleGroup={(u) => setOpenGroups((s) => toggle(s, u))}
                granted={granted}
                onTick={(uid, on) => setEdits((prev) => ({ ...prev, [uid]: on }))} />
            )}

            {/* SAVE AND DISCARD PER MAP. Dattatray: *"If there are 2 permission
                maps opened, both of them shall have their discard and save
                button. Right now there is only one!"*

                One footer for two maps meant a button under the second map's
                rows that silently also wrote the first - and with the two
                collapsible it could sit under a map the operator could not
                even see.

                THIS IS NOT JUST A SECOND BUTTON. The endpoint REPLACES the
                whole set for the space, across every map, so saving map A has
                to carry map B's STORED values - not B's unsaved edits, which
                belong to B's own button. `saveMap` builds it that way, and
                `test_saving_one_map_leaves_the_others_edits_alone` is the
                reason to trust it.

                Shown while the map is open OR holds unsaved edits: collapsing
                an edited map must not hide the only way to save it. */}
            {mayAuthor && (mOpen || unsaved > 0) && (
              <ActionRow top={10}>
                <span className="cap">
                  {mineOn} granted{unsaved ? ` · ${unsaved} unsaved` : ""}
                </span>
                <button className="btn red"
                  disabled={!unsaved || save.isPending}
                  onClick={() => setEdits((prev) => {
                    const next = { ...prev };
                    for (const g of m.groups) {
                      for (const p of g.permissions) delete next[p.perm_uid];
                    }
                    return next;
                  })}>Discard</button>
                <button className="btn teal"
                  disabled={!unsaved || save.isPending}
                  onClick={() => save.mutate(m.map_id)}>
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </ActionRow>
            )}
          </div>
        );
      })}
    </>
  );
}

/** The vocabulary, edited as a FORM — Fabric's shape, inline (D-89, D-102).
 *
 *  Dattatray: *"the Fabric UI for permission map is so much better. You just
 *  fucked up the orbit permission UI to the max."* He is right, and the
 *  comparison is exact, so it is worth writing down what the difference
 *  actually was rather than calling it taste.
 *
 *  WHAT WAS BUILT HERE FIRST: a read-only list, with `Rename` and `Deprecate`
 *  on every row and a request per click. Cluster carries 26 permissions across
 *  2 maps, so the screen held about 78 buttons, and fixing one typo was a
 *  click to enter an input, a click to save, and a round trip.
 *
 *  WHAT FABRIC DOES: the map IS the form. Every id and name is an input you
 *  type into, a single trash icon per row removes it, `+ Add group` and
 *  `+ Add permission` are links, and ONE Save merges the lot —
 *  *"save merges new permissions, updates names, marks deleted IDs
 *  deprecated."* Nothing round-trips until you press it.
 *
 *  So this is that, in Orbit's tokens, with three differences that are Orbit's
 *  rules rather than preferences:
 *
 *    NO POPUP. *"Here we need it without the pop up."* Fabric's is a modal
 *    over the products list; this is the page.
 *
 *    THE NUMBERS ARE FROZEN once saved. Fabric lets you edit an id; Orbit
 *    shows it read-only, because every deployed bench compares against that
 *    integer. A NEW row's id is editable — it has not been promised to
 *    anyone yet.
 *
 *    THE TRASH DEPRECATES, it does not delete. Fabric hard-deletes a map that
 *    leaves the payload; Orbit cannot — `space_definition_permissions`, the
 *    company overrides and the user overrides all point at `perm_uid`, so a
 *    delete would cascade three tables and silently re-mean a number. A
 *    removed row goes struck-through and comes back with one click until you
 *    save.
 */
type DraftPerm = { perm_uid?: string; permission_id: string; name: string;
                   deprecated: boolean; removed: boolean };
type DraftGroup = { group_uid?: string; group_id: string; name: string;
                    deprecated: boolean; removed: boolean; permissions: DraftPerm[] };
type Draft = { name: string; groups: DraftGroup[] };

function toDraft(m: PermMapRow): Draft {
  return {
    name: m.name,
    groups: m.groups.map((g) => ({
      group_uid: g.group_uid, group_id: String(g.group_id), name: g.name,
      deprecated: g.deprecated, removed: g.deprecated,
      permissions: g.permissions.map((p) => ({
        perm_uid: p.perm_uid, permission_id: String(p.permission_id),
        name: p.name, deprecated: p.deprecated, removed: p.deprecated,
      })),
    })),
  };
}

/** A cell input. `frozen` renders the value rather than a control, because a
 *  disabled input still LOOKS like something you could focus and fix. */
function Cell({ value, onChange, placeholder, frozen = false, mono = false, width }: {
  value: string; onChange?: (v: string) => void; placeholder?: string;
  frozen?: boolean; mono?: boolean; width?: number;
}) {
  if (frozen) {
    return (
      <span className={mono ? "mono muted" : "muted"}
        title="the number a deployed bench compares against - deprecate and add instead"
        style={{ padding: "6px 2px", width, display: "inline-block" }}>{value}</span>
    );
  }
  return (
    <SearchInput value={value} placeholder={placeholder} mono={mono}
      style={{ width, minWidth: 0 }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)} />
  );
}

/** Remove / restore, as one icon. Struck-through rather than hidden, so you
 *  can see what a Save is about to do before you press it. */
function Bin({ removed, onClick, title }: {
  removed: boolean; onClick: () => void; title: string;
}) {
  return (
    <button className={removed ? "btn ghost icon" : "btn red icon"} onClick={onClick}
      title={removed ? "keep it after all" : title}>
      <Icon name={removed ? "restore" : "delete"} />
    </button>
  );
}

function AddLink({ children, onClick, indent = 0 }: {
  children: React.ReactNode; onClick: () => void; indent?: number;
}) {
  return (
    <button onClick={onClick}
      style={{ background: "none", border: "none", cursor: "pointer",
        color: "var(--teal)", fontSize: 12, fontWeight: 700, padding: "6px 0",
        marginLeft: indent, textAlign: "left" }}>
      {children}
    </button>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <span aria-hidden style={{ display: "inline-block", width: 12,
      transform: open ? "rotate(90deg)" : undefined,
      transition: "transform .12s ease", color: "var(--t-dim)" }}>▸</span>
  );
}


/** What a SPACE grants — the same tree, read-only, with a tick.
 *
 *  A SEPARATE COMPONENT FROM THE EDITOR, and the split is the question each
 *  answers. The editor changes what the vocabulary IS; this changes what one
 *  space grants OF it, and it must never offer to rename or remove anything —
 *  a stray keystroke while ticking boxes must not be able to re-word a
 *  permission every bench reads.
 */
function GrantTree({ map, groupsOpen, onToggleGroup, granted, onTick, mayAuthor }: {
  map: SpacePermMapRow;
  groupsOpen: Set<string>;
  onToggleGroup: (uid: string) => void;
  granted: (p: Tickable) => boolean;
  onTick: (permUid: string, on: boolean) => void;
  mayAuthor: boolean;
}) {
  if (!map.groups.length) {
    return <div className="panel panel-pad cap">No groups in this map yet.</div>;
  }
  return (
    <div className="panel" style={{ padding: "10px 14px", marginBottom: 10 }}>
      {map.groups.map((g) => {
        const open = groupsOpen.has(g.group_uid);
        const on = g.permissions.filter((p) => granted(p as Tickable)).length;
        return (
          <div key={g.group_uid} style={{ marginBottom: 6 }}>
            <div onClick={() => onToggleGroup(g.group_uid)}
              style={{ display: "flex", alignItems: "center", gap: 10,
                cursor: "pointer", padding: "4px 0" }}>
              <Caret open={open} />
              <span className="mono" style={{ width: 54 }}>{g.group_id}</span>
              <span style={{ fontWeight: 700,
                textDecoration: g.deprecated ? "line-through" : undefined }}>{g.name}</span>
              {/* THE COUNT IS PER TOOL, which is the only level it means
                  anything at - a total across tools would be an addition of
                  two different vocabularies (D-89). */}
              <span className="cap" style={{ color: "var(--t-dim)" }}>
                {on} of {g.permissions.length} granted
              </span>
            </div>
            {open && (
              <div style={{ marginLeft: 26, paddingLeft: 14,
                borderLeft: "2px solid var(--line2)" }}>
                {g.permissions.map((p) => (
                  <label key={p.perm_uid}
                    style={{ display: "grid",
                      gridTemplateColumns: "28px 70px minmax(0,1fr)",
                      gap: 8, alignItems: "center", padding: "3px 0",
                      cursor: mayAuthor ? "pointer" : "default" }}
                    title={p.deprecated && !granted(p as Tickable)
                      ? "Deprecated - it can be removed from a space, not added to one"
                      : undefined}>
                    <input type="checkbox" checked={granted(p as Tickable)}
                      // A DEPRECATED permission can be taken AWAY but not newly
                      // given: granting one hands a bench a number the product
                      // has already stopped meaning anything by.
                      disabled={!mayAuthor || (p.deprecated && !granted(p as Tickable))}
                      onChange={(e) => onTick(p.perm_uid, e.target.checked)} />
                    <span className="mono muted">
                      {p.permission_id}
                    </span>
                    <span style={{ textDecoration: p.deprecated ? "line-through" : undefined,
                      color: p.deprecated ? "var(--t-dim)" : undefined }}>{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/** One map's editor. Closed by default; the header says what it holds. */
function MapEditor({ kind, map, mayAuthor }: {
  kind: string; map: PermMapRow; mayAuthor: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [d, setD] = useState<Draft>(() => toDraft(map));
  /** DEPRECATED ROWS ARE HISTORY, and history does not belong in the middle of
   *  the list you are editing. Dattatray, looking at the cluster map: *"I can
   *  still see 100 - BMS live data in the cluster product vocab."* It was a
   *  Fabric mis-import, deprecated back in June, and it sat between App Conf
   *  and Mfg Config looking like something you had to deal with.
   *
   *  HIDDEN, NOT DELETED. The row has to survive — a deployed bench may still
   *  hold the number, and `space_definition_permissions` and both override
   *  tables point at `perm_uid`. So it is behind a count you can open, which
   *  is also the only way to bring one back. */
  const [showGone, setShowGone] = useState(false);
  // The server's copy is the truth; a refetch after a save must not be read as
  // a pending edit.
  useEffect(() => { setD(toDraft(map)); }, [map]);

  const save = useMutation({
    mutationFn: () => products.saveMap(kind, map.map_id, {
      name: d.name.trim(),
      // A REMOVED ROW IS SIMPLY ABSENT. The server deprecates what it does not
      // see, which is the same contract Fabric's editor posts against.
      groups: d.groups.filter((g) => !g.removed).map((g) => ({
        group_id: Number(g.group_id),
        name: g.name.trim(),
        permissions: g.permissions.filter((p) => !p.removed).map((p) => ({
          permission_id: Number(p.permission_id), name: p.name.trim(),
        })),
      })),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
    },
  });

  const live = (g: DraftGroup) => g.permissions.filter((p) => !p.removed).length;
  const liveGroups = d.groups.filter((g) => !g.removed);
  const totalPerms = liveGroups.reduce((n, g) => n + live(g), 0);
  const dirty = JSON.stringify(d) !== JSON.stringify(toDraft(map));

  const setGroup = (i: number, patch: Partial<DraftGroup>) =>
    setD((s) => ({ ...s, groups: s.groups.map((g, j) => j === i ? { ...g, ...patch } : g) }));
  const setPerm = (i: number, j: number, patch: Partial<DraftPerm>) =>
    setD((s) => ({ ...s, groups: s.groups.map((g, gi) => gi !== i ? g : {
      ...g, permissions: g.permissions.map((p, pi) => pi === j ? { ...p, ...patch } : p) } ) }));

  const toggleGroup = (uid: string) => setOpenGroups((s) => {
    const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n;
  });

  return (
    <div className="panel" style={{ marginBottom: 12, overflow: "hidden" }}>
      {/* ---- the map header. The whole strip toggles, because a 12px caret
              is a target people miss. ---------------------------------- */}
      <div onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
          padding: "12px 16px", borderBottom: open ? "1px solid var(--line)" : undefined }}>
        <Caret open={open} />
        <span style={{ fontWeight: 700 }}>{map.name}</span>
        <span className="mono cap">{map.tool}</span>
        {/* WHAT IT HIDES, on the closed row. Collapsing is only acceptable if
            the header says what is inside. */}
        <span className="cap" style={{ color: "var(--t-dim)" }}>
          {liveGroups.length} group{liveGroups.length === 1 ? "" : "s"} · {totalPerms} permission{totalPerms === 1 ? "" : "s"}
        </span>
        {dirty && <Chip tone="warn">unsaved</Chip>}
      </div>

      {open && (
        <div style={{ padding: "14px 16px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10,
            marginBottom: 14, flexWrap: "wrap" }}>
            <span className="cap">Map name</span>
            <Cell value={d.name} width={280}
              onChange={(v) => setD((s) => ({ ...s, name: v }))} />
            {/* THE SLUG IS SHOWN AND NOT OFFERED: it is what a tool calls
                itself when it asks for its vocabulary, so seeing it matters
                and editing it must not be possible. */}
            <span className="cap">slug <span className="mono">{map.tool}</span></span>
          </div>

          {d.groups.map((g, i) => ({ g, i }))
            .filter(({ g }) => showGone || !(g.deprecated && g.removed))
            .map(({ g, i }) => {
            const gOpen = openGroups.has(g.group_uid ?? `new-${i}`);
            const key = g.group_uid ?? `new-${i}`;
            return (
              <div key={key} style={{ marginBottom: 10, opacity: g.removed ? 0.55 : 1 }}>
                <div style={{ display: "grid",
                  gridTemplateColumns: "20px 90px minmax(0,1fr) auto auto",
                  gap: 8, alignItems: "center" }}>
                  <span onClick={() => toggleGroup(key)} style={{ cursor: "pointer" }}>
                    <Caret open={gOpen} />
                  </span>
                  <Cell value={g.group_id} mono frozen={!!g.group_uid} placeholder="ID"
                    onChange={(v) => setGroup(i, { group_id: v.replace(/\D/g, "") })} />
                  <Cell value={g.name} placeholder="Group name"
                    onChange={(v) => setGroup(i, { name: v })} />
                  <span className="cap" style={{ color: "var(--t-dim)" }}>
                    {live(g)} permission{live(g) === 1 ? "" : "s"}
                  </span>
                  {mayAuthor && <Bin removed={g.removed} title="deprecate this group and its permissions"
                    onClick={() => setGroup(i, { removed: !g.removed })} />}
                </div>

                {gOpen && (
                  <div style={{ marginLeft: 26, paddingLeft: 14,
                    borderLeft: "2px solid var(--line2)", marginTop: 6 }}>
                    {g.permissions.map((p, j) => ({ p, j }))
                      .filter(({ p }) => showGone || !(p.deprecated && p.removed))
                      .map(({ p, j }) => (
                      <div key={p.perm_uid ?? `new-${j}`}
                        style={{ display: "grid",
                          gridTemplateColumns: "90px minmax(0,1fr) auto",
                          gap: 8, alignItems: "center", padding: "3px 0",
                          opacity: p.removed ? 0.55 : 1 }}>
                        <Cell value={p.permission_id} mono frozen={!!p.perm_uid}
                          placeholder="ID"
                          onChange={(v) => setPerm(i, j, { permission_id: v.replace(/\D/g, "") })} />
                        <Cell value={p.name} placeholder="Permission name"
                          onChange={(v) => setPerm(i, j, { name: v })} />
                        {mayAuthor && <Bin removed={p.removed} title="deprecate this permission"
                          onClick={() => setPerm(i, j, { removed: !p.removed })} />}
                      </div>
                    ))}
                    {mayAuthor && !g.removed && (
                      <AddLink onClick={() => setGroup(i, {
                        permissions: [...g.permissions, {
                          permission_id: "", name: "", deprecated: false, removed: false }],
                      })}>+ Add permission</AddLink>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* The count is always shown; the rows are not. A hidden thing with
              no number is a thing you find out about by accident. */}
          {d.groups.some((g) => g.deprecated && g.removed) && (
            <AddLink onClick={() => setShowGone((v) => !v)}>
              {showGone ? "Hide" : "Show"} {d.groups.filter((g) => g.deprecated && g.removed).length} deprecated
            </AddLink>
          )}

          {mayAuthor && (
            <AddLink onClick={() => {
              const uid = `new-${d.groups.length}`;
              setD((s) => ({ ...s, groups: [...s.groups, {
                group_id: "", name: "", deprecated: false, removed: false,
                permissions: [] }] }));
              setOpenGroups((s) => new Set(s).add(uid));
            }}>+ Add group</AddLink>
          )}

          <ErrorNote panel error={save.error} />

          {mayAuthor && (
            <ActionRow top={10}>
              <span className="cap" style={{ color: "var(--t-dim)" }}>
                Saving adds what is new, updates names, and deprecates what you
                removed - nothing is deleted, because benches hold these numbers.
              </span>
              <button className="btn red" disabled={!dirty || save.isPending}
                onClick={() => setD(toDraft(map))}>Discard</button>
              <button className="btn teal" disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </ActionRow>
          )}
        </div>
      )}
    </div>
  );
}


function Vocabulary({ kind, tool, mayAuthor }: {
  kind: string; tool: string; mayAuthor: boolean;
}) {
  const q = usePermissionMaps(kind);
  const maps = (q.data?.maps ?? []).filter((m) => !tool || m.tool === tool);

  if (q.isLoading) return <div className="panel panel-pad cap">Loading…</div>;
  if (!maps.length) {
    return <div className="panel panel-pad cap">
      No permission map for this product yet.
    </div>;
  }
  return (
    <>
      {maps.map((m) => (
        <MapEditor key={m.map_id} kind={kind} map={m} mayAuthor={mayAuthor} />
      ))}
    </>
  );
}


/** The three authoring forms, all INLINE. Dattatray: "No Pop up."
 *
 *  A modal was wrong here for a specific reason, not just a style one: each
 *  of these adds something INTO a thing already on screen — a map into a
 *  product, a group into a map, a permission into a group. Covering that
 *  thing with a dialog hides the context the number has to be unique within,
 *  which is exactly what the operator needs to see while typing it.
 */
function InlineForm({ children, onCancel, onGo, go, busy, err }: {
  children: React.ReactNode; onCancel: () => void; onGo: () => void;
  go: string; busy: boolean; err: unknown;
}) {
  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      {children}
      <ScopeAction>
        <span style={{ display: "flex", gap: 8 }}>
          <button className="btn teal" disabled={busy} onClick={onGo}>
            {busy ? "Saving…" : go}
          </button>
          <button className="btn ghost" onClick={onCancel}>Cancel</button>
        </span>
      </ScopeAction>
      {err ? (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(err as Error).message}
        </div>
      ) : null}
    </div>
  );
}

function AddMapRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [tool, setTool] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addMap(kind, { tool, name: name || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add map"
      busy={!tool || add.isPending} err={add.error}>
      <ScopeField label="Tool slug" hint="what the tool identifies itself as">
        <SearchInput value={tool} autoFocus mono placeholder="uds_tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTool(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="UDS Tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}


